export type RiskReasonCarrier = {
  riskReasonCodes?: string[] | null;
  risk_reason_codes?: string[] | null;
  riskMetrics?: {
    insufficientDataReasons?: string[] | null;
    insufficient_data_reasons?: string[] | null;
  } | null;
  risk_metrics?: {
    insufficientDataReasons?: string[] | null;
    insufficient_data_reasons?: string[] | null;
  } | null;
};

export function readRiskReasonCodes(carrier: RiskReasonCarrier): string[] {
  const codes = carrier.riskReasonCodes ?? carrier.risk_reason_codes ?? [];
  return Array.isArray(codes)
    ? codes.filter((code): code is string => typeof code === "string" && code.trim().length > 0)
    : [];
}

export function readRiskMetricsInsufficientReasons(carrier: RiskReasonCarrier): string[] {
  const metrics = carrier.riskMetrics ?? carrier.risk_metrics;
  const reasons = metrics?.insufficientDataReasons ?? metrics?.insufficient_data_reasons ?? [];
  return Array.isArray(reasons)
    ? reasons.filter((reason): reason is string => typeof reason === "string" && reason.trim().length > 0)
    : [];
}

function mapRiskReasonCodeToLabel(code: string): string {
  switch (code.trim().toUpperCase()) {
    case "PAST_PLANNED_END_NOT_COMPLETE":
      return "The due date is before today, and the latest update is dated on or after the due date but the item is not complete.";
    case "OVERDUE_DATA_NEEDS_UPDATE":
      return "The due date is before today, but the latest update is dated before it. Completion is unconfirmed, so review the recorded status and update the data. This does not confirm an actual delay.";
    case "NEAR_DUE_NOT_STARTED":
      return "The due date is today or tomorrow, the status is Not started, Undecided, Open, missing or unrecognized, and the item was updated today or yesterday.";
    case "REPORTED_DELAYED_STATUS":
      return "The source status explicitly says Delayed or Overdue; this is a reported status, not an inference from dates.";
      case "NOT_STARTED_NEAR_DUE":
      return "Activity has not started and is approaching its planned completion date";
    case "PROGRESS_BEHIND_SCHEDULE":
      return "Reported progress is materially behind the elapsed plan timeline";
    case "FORECAST_AFTER_PLANNED_END":
      return "Forecast completion is later than the planned end date";
    case "FORECAST_DATE_SLIPPING":
      return "The forecast completion date moved later than the previous update.";
    case "DEPENDENCY_AT_RISK":
      return "A linked dependency is at risk or delayed";
    case "BLOCKING_RAID_ITEM":
      return "A linked unresolved RAID item may be blocking delivery";
    case "NO_OWNER_NEAR_START":
      return "Activity is close to or past planned start without an assigned owner";
    case "INSUFFICIENT_PLANNING_DATA":
      return "Core planning data is missing or inconsistent";
    default:
      return code.replace(/_/g, " ").toLowerCase();
  }
}

function mapInsufficientDataReason(reason: string): string {
  switch (reason.trim().toUpperCase()) {
    case "STATUS_MISSING":
      return "status missing";
    case "PLANNED_START_MISSING":
      return "planned start date missing";
    case "PLANNED_END_MISSING":
      return "planned end date missing";
    case "PLANNED_END_BEFORE_START":
      return "planned end date is before planned start date";
    default:
      return reason.replace(/_/g, " ").toLowerCase();
  }
}

export function buildInferredReasonLabel(
  reasonCodes: string[],
  insufficientDataReasons: string[] = [],
  fallback?: string
): string | undefined {
  if (reasonCodes.length === 0) {
    return fallback;
  }

  const labels = reasonCodes.map(mapRiskReasonCodeToLabel);
  if (reasonCodes.some((code) => code.trim().toUpperCase() === "INSUFFICIENT_PLANNING_DATA")) {
    const mappedReasons = insufficientDataReasons.map(mapInsufficientDataReason);
    if (mappedReasons.length > 0) {
      labels.push(`Details: ${mappedReasons.join(", ")}`);
    }
  }

  return labels.join("; ");
}
